import {
    LayoutGrid,
    Spinner,
    Card,
    Button,
    Text,
    Heading,
    Flex
} from '@asktia/tia-ui'
import React, { useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Divider } from 'src/components/Blocks'
import { View } from 'src/components/Blocks/View'
import { CardInput, CreditCardForm } from 'src/components/CreditCardForm'
import Markdown from 'src/components/Markdown'
import {
    SuccessAnimation,
    AnimationVariant
} from 'src/components/SuccessAnimation'
import { Show404 } from 'src/flows/404'
import { BookingCardDetails } from 'src/flows/AppointmentBooking/BookingCard'
import { useBookingFlow } from 'src/flows/AppointmentBooking/useBookingFlow'
import { useAmpli, useHomeMarket, useUserInfo } from 'src/hooks'
import { useAppointmentCancellationPolicy } from 'src/hooks/useAppointmentCancellationPolicy'
import { useUpdateAccountCard } from 'src/hooks/useUpdateAccountCard'
import logger from 'src/logger'
import { usePaymentForm } from 'src/components/StripeWrapper/PaymentForm'
import { StripeCardElementChangeEvent } from '@stripe/stripe-js'

// we need the  prop because there is currently
// no good way to find out whether an appointmentProfile is
// on-demand from our API
export const BookingReview = () => {
    const {
        appointmentProfile,
        slot,
        // currently selected goal
        selectedGoal,
        // goal from URL params or similar
        preselectedGoal,
        selectedTreatments,
        isLoading,
        isBooking,
        isBookingSuccess,
        rescheduleAppointmentUuid,
        bookSlot,
        appointmentSuggestionUuid
    } = useBookingFlow()
    const submitCardRef = React.useRef<(() => void) | null>(null)
    const [updatingCard, setUpdatingCard] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>()
    const [cardValid, setCardValid] = useState(true)
    const { user } = useUserInfo()
    const updateAccountCard = useUpdateAccountCard()
    const { paymentInformationSaved } = useAmpli()

    const { appointmentCancellationPolicy } = useAppointmentCancellationPolicy(
        appointmentProfile?.appointmentProfileUuid
    )
    const { appointmentReviewViewed, bookingConfirmationCtaClicked } =
        useAmpli()

    const appointmentGoal =
        selectedGoal && selectedGoal.id ? `${selectedGoal.id}` : undefined

    const { homeMarket } = useHomeMarket()

    const locationName = useMemo(() => {
        switch (homeMarket?.name) {
            case 'New York City':
                return 'New York State'
            case 'Los Angeles':
            case 'San Francisco':
                return 'California'
            case 'Phoenix':
                return 'Arizona'
            default:
                return 'your home state'
        }
    }, [homeMarket?.name])

    const locationCopy = `Heads up! For compliance reasons, you'll need to be physically present in ${locationName} at the time of this virtual appointment.`

    useEffect(() => {
        if (appointmentProfile) {
            appointmentReviewViewed(
                appointmentProfile,
                appointmentSuggestionUuid,
                appointmentGoal,
                preselectedGoal?.id
            )
        }
    }, [
        appointmentReviewViewed,
        appointmentProfile,
        appointmentSuggestionUuid,
        appointmentGoal,
        preselectedGoal
    ])

    useEffect(() => {
        if (!user?.rawCard) {
            setCardValid(false)
        }
    }, [setCardValid])

    const handleCreditCardChange = (event: StripeCardElementChangeEvent) => {
        setCardValid(event.complete)
    }

    if (isLoading) {
        return <Spinner />
    }

    if (!appointmentProfile || !slot) {
        return <Show404 />
    }

    if (isBookingSuccess) {
        // Transition to next page will happen on hook
        return <BookingAnimation variant={'booking'} />
    }

    const confirmBooking = () => {
        if (!rescheduleAppointmentUuid && appointmentProfile) {
            bookingConfirmationCtaClicked(
                appointmentProfile,
                appointmentSuggestionUuid,
                appointmentGoal
            )
        }

        if (slot) {
            bookSlot(slot)
        }
    }

    const onCardSuccess = async (nonce: string, postalCode: string) => {
        try {
            await updateAccountCard.mutateAsync({
                nonce,
                idempotencyKey: uuidv4(),
                postalCode
            })
            paymentInformationSaved()

            confirmBooking()
        } catch (error) {
            logger.error('Error updating account card:', error)
        } finally {
            setUpdatingCard(false)
        }
    }

    async function onCtaClick() {
        if (!appointmentProfile) {
            // no-op when no appointment profile
            // this shouldn't be possible
            return
        }

        try {
            /* NEW FLOW:
             1. If user has no card: Submit card → Return early → onCardSuccess → Continue booking there
             2. If user has card: Skip card submission → Continue booking here directly
            */
            if (!user?.rawCard) {
                logger.info('No card found, submitting card details')
                setUpdatingCard(true)

                if (submitCardRef.current) {
                    try {
                        submitCardRef.current()
                        return
                    } catch (error) {
                        setUpdatingCard(false)
                    }
                } else {
                    setUpdatingCard(false)
                }
            }

            confirmBooking()
        } catch (error) {
            logger.error(error)
        }
    }

    const cardError = (errors: string[]) => {
        logger.error(errors.join(', '))
        setErrorMessage(errors.join(', '))
    }

    const bookingCta = `Confirm & ${
        rescheduleAppointmentUuid ? 'Reschedule' : 'Book'
    }`

    return (
        <View withFooter>
            <LayoutGrid columns="one" variant="condensed">
                <BookingCardDetails
                    appointmentProfile={appointmentProfile!}
                    slot={slot}
                    selectedTreatments={selectedTreatments}
                    hideDescription
                />

                {appointmentProfile.emergencyHelpText && (
                    <>
                        {appointmentProfile.emergencyHelpText}
                        <Divider
                            sx={{ mt: 0, mb: 0, bg: 'secondaryBackground' }}
                        />
                    </>
                )}

                {slot?.location === 'virtual' && (
                    <Card variant="info" sx={{ mt: 4 }}>
                        <Text color="putty">{locationCopy}</Text>
                    </Card>
                )}

                <Markdown
                    text={appointmentCancellationPolicy?.cancellationCopy || ''}
                    sx={{
                        variant: 'text.supportingInfo',
                        mt: 4
                    }}
                />
                {!user?.rawCard && (
                    <Flex mt={4} sx={{ flexDirection: 'column' }}>
                        <CreditCardForm
                            onSuccess={onCardSuccess}
                            onError={cardError}
                            showCardOnLoad={true}
                        >
                            <Card
                                sx={{
                                    mb: 4,
                                    pb: 4,
                                    borderColor: undefined,
                                    backgroundColor: 'secondaryBackground'
                                }}
                            >
                                <Heading variant="cardTitle">
                                    Add new credit or debit card
                                </Heading>
                                <CardInput
                                    onChange={event => {
                                        setErrorMessage('')
                                        handleCreditCardChange(event)
                                    }}
                                />
                                {errorMessage && (
                                    <Flex
                                        sx={{
                                            width: '100%',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Text
                                            sx={{
                                                fontSize: 0,
                                                textAlign: 'center',
                                                color: 'inputOutlineError'
                                            }}
                                        >
                                            {errorMessage}
                                        </Text>
                                    </Flex>
                                )}
                            </Card>
                            <SubmitCardCapture submitRef={submitCardRef} />
                        </CreditCardForm>
                    </Flex>
                )}

                <Button
                    fullWidth
                    mt={5}
                    onClick={onCtaClick}
                    loading={isBooking || updatingCard}
                    disabled={!cardValid || updatingCard}
                >
                    {user?.membershipSignupStatus === 'preview'
                        ? 'Resume signup'
                        : bookingCta}
                </Button>
            </LayoutGrid>
        </View>
    )
}

export const BookingAnimation = React.memo(
    ({ variant }: { variant: AnimationVariant }) => (
        <SuccessAnimation variant={variant} />
    )
)

/* Helper component to capture the onSubmitCard function from the context,
as we're running onSubmitCard outside the context

*/
const SubmitCardCapture = ({
    submitRef
}: {
    submitRef: React.MutableRefObject<(() => void) | null>
}) => {
    const { onSubmitCard } = usePaymentForm()

    useEffect(() => {
        submitRef.current = onSubmitCard
        return () => {
            submitRef.current = null
        }
    }, [onSubmitCard, submitRef])

    return null
}
